import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileText,
  Loader2,
  RotateCw,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { Card, Tag, type TagTone } from '@/components/ui';
import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/api';
import {
  deleteDocument,
  documentDownloadUrl,
  listMatterDocuments,
  reextractDocument,
  uploadMatterDocument,
  type DocumentSummary,
  type DocumentExtractionStatus,
} from '@/lib/api-client';

// Documents panel (P3 Section 3.7). Lists a matter's uploaded documents with
// extraction-status badges, an upload button + drag-and-drop zone, a 2s poll
// while anything is still extracting, click-to-download, and soft delete.

interface DocumentsPanelProps {
  matterId: string;
}

const STATUS_META: Record<DocumentExtractionStatus, { label: string; tone: TagTone }> = {
  PENDING: { label: 'Queued', tone: 'muted' },
  EXTRACTING: { label: 'Extracting', tone: 'accent' },
  READY: { label: 'Ready', tone: 'accent' },
  FAILED: { label: 'Failed', tone: 'neutral' },
};

export function DocumentsPanel({ matterId }: DocumentsPanelProps) {
  const [documents, setDocuments] = useState<DocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!matterId) {
      return;
    }
    try {
      const docs = await listMatterDocuments(matterId);
      setDocuments(docs);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load documents.');
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  // Poll every 2s while any document is still being processed (Section 3.7).
  const hasPending = documents.some(
    (d) => d.extractionStatus === 'PENDING' || d.extractionStatus === 'EXTRACTING',
  );
  useEffect(() => {
    if (!hasPending) {
      return;
    }
    const timer = setInterval(() => void load(), 2000);
    return () => clearInterval(timer);
  }, [hasPending, load]);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || !matterId) {
        return;
      }
      setUploading(true);
      setError(null);
      setNotice(null);
      const failures: string[] = [];
      for (const file of Array.from(files)) {
        try {
          await uploadMatterDocument(matterId, file);
        } catch (err) {
          const msg =
            err instanceof ApiError
              ? err.code === 'DUPLICATE'
                ? `${file.name}: already uploaded`
                : `${file.name}: ${err.message}`
              : `${file.name}: upload failed`;
          failures.push(msg);
        }
      }
      setUploading(false);
      if (failures.length > 0) {
        setError(failures.join(' · '));
      } else {
        setNotice('Uploaded. Extraction runs in the background.');
      }
      await load();
    },
    [matterId, load],
  );

  async function remove(doc: DocumentSummary): Promise<void> {
    const ok = window.confirm(
      `This will soft delete ${doc.filename}. It will be excluded from future answers. Continue?`,
    );
    if (!ok) {
      return;
    }
    try {
      await deleteDocument(doc.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  }

  async function retry(doc: DocumentSummary): Promise<void> {
    try {
      await reextractDocument(doc.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Re-extract failed.');
    }
  }

  return (
    <div className="space-y-4">
      <Dropzone
        dragOver={dragOver}
        uploading={uploading}
        onPick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
      />
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          void handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      {notice ? <p className="text-sm text-emerald-400">{notice}</p> : null}

      <Card variant="default" padding="md" className="space-y-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
          Documents ({documents.length})
        </p>
        {loading ? (
          <p className="text-sm text-stone-500">Loading documents...</p>
        ) : documents.length === 0 ? (
          <p className="text-sm text-stone-500">
            No documents yet. Drop a PDF, image, Word doc, email, or spreadsheet above.
          </p>
        ) : (
          <ul className="space-y-2">
            {documents.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center gap-3 rounded-md border border-stone-800/60 bg-stone-900/60 px-3 py-2"
              >
                <FileText className="h-4 w-4 shrink-0 text-stone-500" aria-hidden />
                <div className="min-w-0 flex-1">
                  <a
                    href={documentDownloadUrl(doc.id)}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-sm text-stone-200 hover:text-amber-400"
                    title={doc.filename}
                  >
                    {doc.filename}
                  </a>
                  <span className="block text-[10px] uppercase tracking-[0.15em] text-stone-500">
                    {formatBytes(doc.sizeBytes)}
                    {doc.tokenEstimate ? ` · ~${doc.tokenEstimate.toLocaleString()} tokens` : ''}
                  </span>
                  {doc.extractionStatus === 'FAILED' && doc.extractionError ? (
                    <span className="mt-0.5 block text-[11px] text-red-400/90">
                      {doc.extractionError}
                    </span>
                  ) : null}
                </div>
                <StatusBadge status={doc.extractionStatus} />
                <div className="flex shrink-0 items-center gap-1">
                  {doc.extractionStatus === 'FAILED' ? (
                    <button
                      type="button"
                      onClick={() => void retry(doc)}
                      aria-label="Re-extract"
                      title="Re-extract"
                      className="rounded p-1 text-stone-500 hover:text-amber-400"
                    >
                      <RotateCw className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                  <a
                    href={documentDownloadUrl(doc.id)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Download"
                    title="Download"
                    className="rounded p-1 text-stone-500 hover:text-amber-400"
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  <button
                    type="button"
                    onClick={() => void remove(doc)}
                    aria-label="Delete"
                    title="Delete"
                    className="rounded p-1 text-stone-500 hover:text-red-400"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

interface DropzoneProps {
  dragOver: boolean;
  uploading: boolean;
  onPick: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
}

function Dropzone({ dragOver, uploading, onPick, onDragOver, onDragLeave, onDrop }: DropzoneProps) {
  return (
    <button
      type="button"
      onClick={onPick}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={cn(
        'flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-6 text-center transition-colors',
        dragOver
          ? 'border-amber-500 bg-amber-950/20'
          : 'border-stone-700 bg-stone-900/40 hover:border-stone-600',
      )}
    >
      {uploading ? (
        <Loader2 className="h-5 w-5 animate-spin text-amber-500" aria-hidden />
      ) : (
        <UploadCloud className="h-5 w-5 text-amber-500" aria-hidden />
      )}
      <span className="text-sm text-stone-300">
        {uploading ? 'Uploading...' : 'Drop a file here, or click to choose'}
      </span>
      <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
        PDF · image · Word · email · spreadsheet · 50 MB max
      </span>
    </button>
  );
}

function StatusBadge({ status }: { status: DocumentExtractionStatus }) {
  const meta = STATUS_META[status];
  const icon =
    status === 'EXTRACTING' ? (
      <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
    ) : status === 'READY' ? (
      <CheckCircle2 className="h-3 w-3" aria-hidden />
    ) : status === 'FAILED' ? (
      <AlertTriangle className="h-3 w-3" aria-hidden />
    ) : undefined;
  return (
    <Tag
      tone={meta.tone}
      leftIcon={icon}
      className={cn('shrink-0', status === 'FAILED' && 'text-red-400')}
    >
      {meta.label}
    </Tag>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
